import { useEffect, useState, type ReactNode } from 'react';
import type { InternalAgentVisibility } from '@localagent/sdk';
import { SettingsMenuRow, SettingsMenuSelect } from '../chat/SettingsMenuRow';

type ProcessMode = 'default' | 'manual' | 'automatic';

interface ChatProcessModeControlProps {
  automatic: boolean;
  inherited: boolean;
  automaticCandidateCount: number | undefined;
  personaName: string;
  internalAgentVisibility: InternalAgentVisibility;
  manualProcessControl: ReactNode;
  onAutomaticChange: (enabled: boolean) => void | Promise<void>;
  onInherit: () => void | Promise<void>;
  onInternalAgentVisibilityChange: (visibility: InternalAgentVisibility) => void | Promise<void>;
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Unable to update execution mode';
}

export function ChatProcessModeControl({
  automatic,
  inherited,
  automaticCandidateCount,
  personaName,
  internalAgentVisibility,
  manualProcessControl,
  onAutomaticChange,
  onInherit,
  onInternalAgentVisibilityChange,
}: ChatProcessModeControlProps) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const selectedMode: ProcessMode = inherited ? 'default' : automatic ? 'automatic' : 'manual';
  const automaticAvailable = automaticCandidateCount !== undefined && automaticCandidateCount > 0;
  const invalidAutomaticState = automatic && automaticCandidateCount === 0;

  useEffect(() => setError(undefined), [automatic, inherited]);

  const updateMode = async (mode: ProcessMode) => {
    setSaving(true);
    setError(undefined);
    try {
      if (mode === 'default') await onInherit();
      else await onAutomaticChange(mode === 'automatic');
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setSaving(false);
    }
  };

  const updateInternalAgentVisibility = async (visibility: InternalAgentVisibility) => {
    setSaving(true);
    setError(undefined);
    try {
      await onInternalAgentVisibilityChange(visibility);
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setSaving(false);
    }
  };

  const automaticLabel =
    automaticCandidateCount === undefined
      ? 'Automatic (checking…)'
      : automaticAvailable
        ? `Automatic · ${automaticCandidateCount} ${automaticCandidateCount === 1 ? 'process' : 'processes'}`
        : 'Automatic (no assigned processes)';

  return (
    <div className="flex flex-col" data-testid="chat-process-settings">
      <SettingsMenuRow
        label="Process selection"
        hint={`Agent default uses ${personaName}'s configured mode. Automatic assesses each message and chooses from the processes assigned to the agent. Manual always uses the process you select.`}
      >
        <SettingsMenuSelect
          data-testid="chat-process-mode"
          aria-label="Process selection"
          value={selectedMode}
          disabled={saving}
          onChange={(event) => void updateMode(event.target.value as ProcessMode)}
        >
          <option value="default">Agent default</option>
          <option value="manual">Manual</option>
          <option value="automatic" disabled={!automaticAvailable}>
            {automaticLabel}
          </option>
        </SettingsMenuSelect>
      </SettingsMenuRow>

      {!automatic && (
        <SettingsMenuRow
          label="Process"
          hint="The process this chat runs when it isn't selecting automatically. Takes effect on the next run."
        >
          {manualProcessControl}
        </SettingsMenuRow>
      )}

      <SettingsMenuRow
        label="Internal agent details"
        hint="How internal agents are named in this chat's activity: friendly names, or the exact system names."
      >
        <SettingsMenuSelect
          data-testid="internal-agent-visibility"
          aria-label="Internal agent details"
          value={internalAgentVisibility}
          disabled={saving}
          onChange={(event) => void updateInternalAgentVisibility(event.target.value as InternalAgentVisibility)}
        >
          <option value="friendly">Friendly names</option>
          <option value="exact">Exact system names</option>
        </SettingsMenuSelect>
      </SettingsMenuRow>

      {automaticCandidateCount === 0 && (
        <p className="px-2 py-1 text-xs text-muted-foreground">
          Assign at least one process to {personaName} to use Automatic.
        </p>
      )}

      {invalidAutomaticState && (
        <div role="alert" className="mx-2 my-1 rounded-md bg-destructive/10 p-2 text-xs text-destructive">
          Automatic mode cannot run until this agent has an assigned process. Choose Manual or configure the agent.
        </div>
      )}

      {error && <div role="alert" className="px-2 py-1 text-xs text-destructive">{error}</div>}
    </div>
  );
}
